import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Check, Star, ChevronLeft, ChevronRight } from "lucide-react";

const PLANS = [
  {
    name: "Essentials",
    price: "$45",
    badge: null,
    features: [
      "Case Review",
      "Case Filing",
      "Basic Search",
      "State E-Filing",
      "Trademark Secured",
    ],
    note: "Govt. filing fee not included",
    cta: "Get Started",
  },
  {
    name: "Enhanced",
    price: "$149",
    badge: null,
    features: [
      "Case Review",
      "Case Filing",
      "Direct Hit Search",
      "Trademark Alert",
      "Trademark Secured",
      "High Satisfaction Rate",
      "Refusal Risk Meter",
    ],
    note: "Govt. filing fee not included",
    cta: "Get Started",
  },
  {
    name: "Deluxe",
    price: "$299",
    badge: "Recommended",
    features: [
      "Case Review",
      "Case Filing",
      "Direct Hit Search",
      "Trademark Alert",
      "Trademark Secured",
      "High Satisfaction Rate",
      "Refusal Risk Meter",
      "Trademark Monitoring",
      "Complete Documentation",
      "Digital File",
      "Dedicated Case Manager",
    ],
    note: "Govt. filing fee not included",
    cta: "Protect My Brand",
  },
  {
    name: "Platinum",
    price: "$349",
    badge: null,
    features: [
      "Case Review",
      "Case Filing",
      "Direct Hit Search",
      "Trademark Alert",
      "Trademark Secured",
      "High Satisfaction Rate",
      "Refusal Risk Meter",
      "Trademark Monitoring",
      "Complete Documentation",
      "Digital File",
      "Dedicated Case Manager",
      "High Approval Rate",
      "Comprehensive Search Report",
      "High Priority Service",
      "Cease & Desist Letter",
    ],
    note: "Govt. filing fee not included",
    cta: "Register My Trademark",
  },
];

export default function PricingSection({ onOpenForm }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const sliderRef = useRef(null);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;
    const handleScroll = () => {
      const scrollLeft = slider.scrollLeft;
      const cardWidth = slider.scrollWidth / PLANS.length;
      setActiveIndex(Math.round(scrollLeft / cardWidth));
    };
    slider.addEventListener("scroll", handleScroll);
    return () => slider.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (dir) => {
    if (!sliderRef.current) return;
    const cardWidth = sliderRef.current.scrollWidth / PLANS.length;
    const next = dir === "left" ? activeIndex - 1 : activeIndex + 1;
    if (next >= 0 && next < PLANS.length) {
      sliderRef.current.scrollTo({ left: cardWidth * next, behavior: "smooth" });
    }
  };

  const scrollToDot = (i) => {
    if (!sliderRef.current) return;
    const cardWidth = sliderRef.current.scrollWidth / PLANS.length;
    sliderRef.current.scrollTo({ left: cardWidth * i, behavior: "smooth" });
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="py-20 md:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A8A] mb-4">
            Pricing Plans
          </span>
          <h2
            data-testid="pricing-heading"
            className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight mb-4"
          >
            Affordable Trademark Registration Packages
          </h2>
          <p className="text-[#475569] text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
            Choose the package that best fits your needs and budget. All plans include professional trademark filing with the USPTO. Our transparent pricing ensures you know exactly what you are paying for with no hidden fees or surprise charges.
          </p>
        </div>

        {/* Mobile arrows */}
        <div className="flex justify-between items-center mb-4 md:hidden px-2">
          <button
            onClick={() => scrollTo("left")}
            data-testid="pricing-arrow-left"
            className="p-2 rounded-full bg-white border border-slate-200 shadow-sm disabled:opacity-30"
            disabled={activeIndex === 0}
          >
            <ChevronLeft className="w-5 h-5 text-slate-700" />
          </button>
          <span className="text-sm text-slate-500 font-medium">
            {activeIndex + 1} / {PLANS.length}
          </span>
          <button
            onClick={() => scrollTo("right")}
            data-testid="pricing-arrow-right"
            className="p-2 rounded-full bg-white border border-slate-200 shadow-sm disabled:opacity-30"
            disabled={activeIndex === PLANS.length - 1}
          >
            <ChevronRight className="w-5 h-5 text-slate-700" />
          </button>
        </div>

        {/* Pricing cards - mobile slider / desktop grid */}
        <div
          ref={sliderRef}
          className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 pb-4 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6 md:overflow-visible md:pb-0"
        >
          {PLANS.map((plan, i) => {
            const isRecommended = plan.badge === "Recommended";
            return (
              <div
                key={plan.name}
                data-testid={`pricing-card-${i}`}
                className={`min-w-[85vw] snap-center md:min-w-0 flex flex-col rounded-lg border-2 transition-all duration-300 ${
                  isRecommended
                    ? "border-[#1E3A8A] shadow-lg shadow-blue-900/10 relative"
                    : "border-slate-200 bg-white hover:shadow-md"
                }`}
              >
                {/* Recommended badge */}
                {isRecommended && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                    <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-[#DC2626] text-white text-xs font-bold uppercase tracking-wider shadow-lg">
                      <Star className="w-3 h-3" /> Recommended
                    </span>
                  </div>
                )}

                {/* Header */}
                <div
                  className={`p-6 text-center border-b ${
                    isRecommended ? "bg-[#1E3A8A] text-white" : "bg-white"
                  }`}
                >
                  <h3 className="font-heading text-lg font-bold mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-heading text-3xl font-extrabold">{plan.price}</span>
                  </div>
                  <p
                    className={`text-xs mt-1 ${
                      isRecommended ? "text-blue-200" : "text-slate-500"
                    }`}
                  >
                    + Federal Filing Fee
                  </p>
                </div>

                {/* Features - scrollable */}
                <div className="flex-1 p-6 overflow-y-auto max-h-[320px] bg-white">
                  <ul className="space-y-3">
                    {plan.features.map((feat) => (
                      <li key={feat} className="flex items-start gap-2.5">
                        <Check className="w-4 h-4 text-[#1E3A8A] mt-0.5 shrink-0" />
                        <span className="text-sm text-[#0F172A]">{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-100 bg-white rounded-b-lg">
                  <Button
                    onClick={onOpenForm}
                    data-testid={`pricing-cta-${i}`}
                    className={`w-full h-11 font-semibold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] ${
                      isRecommended
                        ? "bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                        : "bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
                    }`}
                  >
                    {plan.cta}
                  </Button>
                  <p className="text-[10px] text-center text-slate-400 mt-2">{plan.note}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile dots */}
        <div className="flex justify-center gap-2 mt-6 md:hidden">
          {PLANS.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollToDot(i)}
              data-testid={`pricing-dot-${i}`}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                activeIndex === i ? "w-6 bg-[#1E3A8A]" : "w-2.5 bg-slate-300"
              }`}
              aria-label={`Go to plan ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
